import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { MealPlan, MealPlanFilters, PlannedMeal } from '@/types/meal-plan'
import type { Recipe } from '@/types/recipe'
import type { ShoppingList, ShoppingListItem } from '@/types/shopping-list'
import { mealPlanApi } from '@/services/meal-plan-api'
import { recipeApi } from '@/services/recipe-api'
import { shoppingListApi } from '@/services/shopping-list-api'

interface MealPlanningState {
  currentMealPlan: MealPlan | null
  mealPlans: MealPlan[]
  plannedMeals: PlannedMeal[]
  shoppingLists: ShoppingList[]
  currentShoppingList: ShoppingList | null
  currentWeekStart: Date
  currentWeekEnd: Date
  loading: boolean
  loadingPlannedMeals: boolean
  loadingShoppingList: boolean
  error: string | null
  filters: MealPlanFilters
  suggestedRecipes: Recipe[]
  loadingSuggestions: boolean
}

interface MealPlanningActions {
  loadMealPlans: (householdId?: string) => Promise<void>
  ensureDefaultMealPlan: (householdId?: string) => Promise<MealPlan | null>
  createMealPlan: (mealPlan: Pick<MealPlan, 'name' | 'description' | 'start_date' | 'end_date' | 'is_template'> & { household_id?: string | null }, householdId?: string) => Promise<MealPlan | null>
  updateMealPlan: (id: string, updates: Partial<MealPlan>) => Promise<MealPlan | null>
  deleteMealPlan: (id: string) => Promise<boolean>
  setCurrentMealPlan: (mealPlan: MealPlan | null) => void
  loadMealPlan: (id: string) => Promise<MealPlan | null>
  loadPlannedMeals: (mealPlanId: string) => Promise<void>
  addPlannedMeal: (plannedMeal: Omit<PlannedMeal, 'id' | 'created_at' | 'updated_at'>) => Promise<PlannedMeal | null>
  updatePlannedMeal: (id: string, updates: Partial<PlannedMeal>) => Promise<PlannedMeal | null>
  removePlannedMeal: (id: string) => Promise<boolean>
  movePlannedMeal: (id: string, newDate: string, newMealType: PlannedMeal['meal_type']) => Promise<boolean>
  assignRecipeToWeek: (recipeId: string, dates: string[], mealType: PlannedMeal['meal_type']) => Promise<boolean>
  clearWeek: (startDate: string, endDate: string) => Promise<boolean>
  duplicateWeek: (sourceStartDate: string, targetStartDate: string) => Promise<boolean>
  generateShoppingList: (mealPlanId: string, startDate?: string, endDate?: string) => Promise<ShoppingList | null>
  loadShoppingList: (mealPlanId: string) => Promise<void>
  updateShoppingListItem: (itemId: string, updates: Partial<ShoppingListItem>) => Promise<boolean>
  addCustomShoppingItem: (_mealPlanId: string, ingredient: string, quantity?: number, unit?: string) => Promise<ShoppingListItem | null>
  removeShoppingListItem: (itemId: string) => Promise<boolean>
  toggleShoppingItemChecked: (itemId: string) => Promise<boolean>
  clearCheckedItems: (shoppingListId: string) => Promise<boolean>
  setCurrentWeek: (startDate: Date) => void
  navigateToWeek: (direction: 'previous' | 'next') => void
  navigateToToday: () => void
  getWeekDates: (startDate?: Date) => { start: Date; end: Date; dates: Date[] }
  loadSuggestedRecipes: (householdId?: string, mealType?: PlannedMeal['meal_type']) => Promise<void>
  setFilters: (filters: MealPlanFilters) => void
  clearFilters: () => void
  getFilteredMealPlans: () => MealPlan[]
  getMealConflicts: (date: string, mealType: PlannedMeal['meal_type']) => PlannedMeal[]
  resolveMealConflict: (existingMealId: string, action: 'replace' | 'duplicate' | 'cancel') => Promise<boolean>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  getMealsForDate: (date: string) => PlannedMeal[]
  getMealsForWeek: (startDate: Date) => Record<string, PlannedMeal[]>
  getRecipeUsageStats: (recipeId: string) => { count: number; lastUsed?: string }
}

type MealPlanningStore = MealPlanningState & MealPlanningActions

const getWeekStart = (date: Date): Date => {
  const copy = new Date(date)
  const day = copy.getDay()
  const diff = copy.getDate() - day + (day === 0 ? -6 : 1)
  copy.setDate(diff)
  copy.setHours(0, 0, 0, 0)
  return copy
}

const getWeekEnd = (date: Date): Date => {
  const start = getWeekStart(date)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  end.setHours(23, 59, 59, 999)
  return end
}

const formatDate = (date: Date) => date.toISOString().split('T')[0]

const DEFAULT_MEAL_PLAN_NAME = 'Mijn Weekplanning'

const pickPrimaryMealPlan = (mealPlans: MealPlan[], householdId?: string) => {
  const normalizedHouseholdId = householdId ?? null
  const contextPlans = mealPlans.filter((mealPlan) => (mealPlan.household_id ?? null) === normalizedHouseholdId)

  if (contextPlans.length === 0) {
    return null
  }

  const defaultPlans = contextPlans.filter((mealPlan) => mealPlan.name === DEFAULT_MEAL_PLAN_NAME)

  if (defaultPlans.length > 0) {
    return [...defaultPlans].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0]
  }

  return [...contextPlans].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0]
}

let ensureDefaultMealPlanPromise: Promise<MealPlan | null> | null = null

export const useMealPlanningStore = create<MealPlanningStore>()(
  persist(
    (set, get) => ({
      currentMealPlan: null,
      mealPlans: [],
      plannedMeals: [],
      shoppingLists: [],
      currentShoppingList: null,
      currentWeekStart: getWeekStart(new Date()),
      currentWeekEnd: getWeekEnd(new Date()),
      loading: false,
      loadingPlannedMeals: false,
      loadingShoppingList: false,
      error: null,
      filters: {},
      suggestedRecipes: [],
      loadingSuggestions: false,

      loadMealPlans: async (householdId) => {
        set({ loading: true, error: null })
        try {
          const mealPlans = await mealPlanApi.getMealPlans(householdId)
          const persistedMealPlan = get().currentMealPlan
          const fallbackMealPlan = pickPrimaryMealPlan(mealPlans, householdId)
          const resolvedCurrentMealPlan = persistedMealPlan
            ? mealPlans.find((mealPlan) => mealPlan.id === persistedMealPlan.id) ?? null
            : fallbackMealPlan

          set({
            mealPlans,
            currentMealPlan: resolvedCurrentMealPlan ?? fallbackMealPlan ?? mealPlans[0] ?? null,
            plannedMeals: [],
            loading: false,
          })

          if (resolvedCurrentMealPlan ?? fallbackMealPlan ?? mealPlans[0]) {
            void get().loadPlannedMeals((resolvedCurrentMealPlan ?? fallbackMealPlan ?? mealPlans[0]).id)
          }
        } catch (error) {
          set({
            mealPlans: [],
            currentMealPlan: null,
            plannedMeals: [],
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load meal plans',
          })
        }
      },

      ensureDefaultMealPlan: async (householdId) => {
        const existing = pickPrimaryMealPlan(get().mealPlans, householdId)
        if (existing) {
          if (get().currentMealPlan?.id !== existing.id) {
            set({ currentMealPlan: existing })
            void get().loadPlannedMeals(existing.id)
          }
          return existing
        }

        if (ensureDefaultMealPlanPromise) {
          return ensureDefaultMealPlanPromise
        }

        ensureDefaultMealPlanPromise = (async () => {
          const weekStart = get().currentWeekStart
          const weekEnd = new Date(weekStart)
          weekEnd.setDate(weekStart.getDate() + 6)

          const created = await get().createMealPlan(
            {
              name: DEFAULT_MEAL_PLAN_NAME,
              description: 'Standaard weekplanning',
              start_date: formatDate(weekStart),
              end_date: formatDate(weekEnd),
              is_template: false,
            },
            householdId
          )

          if (created) {
            set({ currentMealPlan: created })
            void get().loadPlannedMeals(created.id)
          }

          return created
        })().finally(() => {
          ensureDefaultMealPlanPromise = null
        })

        return ensureDefaultMealPlanPromise
      },

      createMealPlan: async (mealPlanData, householdId) => {
        set({ loading: true, error: null })
        try {
          const created = await mealPlanApi.createMealPlan({
            ...mealPlanData,
            household_id: householdId ?? mealPlanData.household_id ?? null,
            plan_type: 'weekly',
          })
          const mealPlan = created
          set((state) => ({
            mealPlans: [mealPlan, ...state.mealPlans],
            currentMealPlan: mealPlan,
            loading: false,
          }))
          return mealPlan
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to create meal plan',
          })
          return null
        }
      },

      updateMealPlan: async (id, updates) => {
        set({ loading: true, error: null })
        try {
          const updated = await mealPlanApi.updateMealPlan(id, updates)
          set((state) => ({
            mealPlans: state.mealPlans.map((mealPlan) => (mealPlan.id === id ? updated : mealPlan)),
            currentMealPlan: state.currentMealPlan?.id === id ? updated : state.currentMealPlan,
            loading: false,
          }))
          return updated
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to update meal plan',
          })
          return null
        }
      },

      deleteMealPlan: async (id) => {
        set({ loading: true, error: null })
        try {
          await mealPlanApi.deleteMealPlan(id)
          set((state) => ({
            mealPlans: state.mealPlans.filter((mealPlan) => mealPlan.id !== id),
            currentMealPlan: state.currentMealPlan?.id === id ? null : state.currentMealPlan,
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to delete meal plan',
          })
          return false
        }
      },

      setCurrentMealPlan: (mealPlan) => {
        set({ currentMealPlan: mealPlan })
        if (mealPlan) {
          void get().loadPlannedMeals(mealPlan.id)
        }
      },

      loadMealPlan: async (id) => {
        set({ loading: true, error: null })
        try {
          const mealPlan = await mealPlanApi.getMealPlan(id)
          set({ currentMealPlan: mealPlan, loading: false })
          return mealPlan
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load meal plan',
          })
          return null
        }
      },

      loadPlannedMeals: async (mealPlanId) => {
        set({ loadingPlannedMeals: true, error: null })
        try {
          const meals = await mealPlanApi.getPlannedMeals(mealPlanId)
          set({ plannedMeals: meals, loadingPlannedMeals: false })
        } catch (error) {
          set({
            plannedMeals: [],
            loadingPlannedMeals: false,
            error: error instanceof Error ? error.message : 'Failed to load planned meals',
          })
        }
      },

      addPlannedMeal: async (plannedMealData) => {
        set({ loadingPlannedMeals: true, error: null })
        try {
          const created = await mealPlanApi.createPlannedMeal(plannedMealData.meal_plan_id, {
            recipe_id: plannedMealData.recipe_id,
            planned_date: plannedMealData.planned_date,
            meal_type: plannedMealData.meal_type,
            planned_servings: plannedMealData.planned_servings,
            notes: plannedMealData.notes,
            assigned_to: plannedMealData.assigned_to,
          })
          set((state) => ({
            plannedMeals: [...state.plannedMeals, created],
            loadingPlannedMeals: false,
          }))
          return created
        } catch (error) {
          set({
            loadingPlannedMeals: false,
            error: error instanceof Error ? error.message : 'Failed to add planned meal',
          })
          return null
        }
      },

      updatePlannedMeal: async (id, updates) => {
        set({ loadingPlannedMeals: true, error: null })
        try {
          const updated = await mealPlanApi.updatePlannedMeal(id, {
            ...(updates.planned_date ? { planned_date: updates.planned_date } : {}),
            ...(updates.planned_servings !== undefined ? { planned_servings: updates.planned_servings } : {}),
            ...(updates.meal_type ? { meal_type: updates.meal_type } : {}),
            ...(updates.notes !== undefined ? { notes: updates.notes } : {}),
            ...(updates.assigned_to !== undefined ? { assigned_to: updates.assigned_to } : {}),
          })
          set((state) => ({
            plannedMeals: state.plannedMeals.map((meal) => (meal.id === id ? updated : meal)),
            loadingPlannedMeals: false,
          }))
          return updated
        } catch (error) {
          set({
            loadingPlannedMeals: false,
            error: error instanceof Error ? error.message : 'Failed to update planned meal',
          })
          return null
        }
      },

      removePlannedMeal: async (id) => {
        set({ loadingPlannedMeals: true, error: null })
        try {
          await mealPlanApi.deletePlannedMeal(id)
          set((state) => ({
            plannedMeals: state.plannedMeals.filter((meal) => meal.id !== id),
            loadingPlannedMeals: false,
          }))
          return true
        } catch (error) {
          set({
            loadingPlannedMeals: false,
            error: error instanceof Error ? error.message : 'Failed to remove planned meal',
          })
          return false
        }
      },

      movePlannedMeal: async (id, newDate, newMealType) => {
        const result = await get().updatePlannedMeal(id, { planned_date: newDate, meal_type: newMealType })
        return result !== null
      },

      assignRecipeToWeek: async (recipeId, dates, mealType) => {
        const currentMealPlan = get().currentMealPlan
        if (!currentMealPlan) {
          return false
        }

        const created = await Promise.all(
          dates.map((date) =>
            get().addPlannedMeal({
              meal_plan_id: currentMealPlan.id,
              recipe_id: recipeId,
              planned_date: date,
              meal_type: mealType,
              planned_servings: 1,
              notes: null,
              assigned_to: null,
            })
          )
        )

        return created.every(Boolean)
      },

      clearWeek: async (startDate, endDate) => {
        const mealsToDelete = get().plannedMeals.filter((meal) => meal.planned_date >= startDate && meal.planned_date <= endDate)
        const results = await Promise.all(mealsToDelete.map((meal) => get().removePlannedMeal(meal.id)))
        return results.every(Boolean)
      },

      duplicateWeek: async (sourceStartDate, targetStartDate) => {
        const currentMealPlan = get().currentMealPlan
        if (!currentMealPlan) {
          return false
        }

        const sourceStart = new Date(sourceStartDate)
        const sourceEnd = new Date(sourceStart)
        sourceEnd.setDate(sourceStart.getDate() + 6)
        const dayDifference = Math.floor(
          (new Date(targetStartDate).getTime() - sourceStart.getTime()) / (1000 * 60 * 60 * 24)
        )

        const mealsToCopy = get().plannedMeals.filter(
          (meal) => meal.planned_date >= sourceStartDate && meal.planned_date <= formatDate(sourceEnd)
        )

        const created = await Promise.all(
          mealsToCopy.map((meal) => {
            const newDate = new Date(meal.planned_date)
            newDate.setDate(newDate.getDate() + dayDifference)
            return get().addPlannedMeal({
              meal_plan_id: currentMealPlan.id,
              recipe_id: meal.recipe_id,
              planned_date: formatDate(newDate),
              meal_type: meal.meal_type,
              planned_servings: meal.planned_servings,
              notes: meal.notes,
              assigned_to: meal.assigned_to,
            })
          })
        )

        return created.every(Boolean)
      },

      generateShoppingList: async (mealPlanId, startDate, endDate) => {
        set({ loadingShoppingList: true, error: null })
        try {
          const shoppingList = await shoppingListApi.generateMealPlanShoppingList(mealPlanId, startDate, endDate)
          set({ currentShoppingList: shoppingList, loadingShoppingList: false })
          return shoppingList
        } catch (error) {
          set({
            loadingShoppingList: false,
            error: error instanceof Error ? error.message : 'Failed to generate shopping list',
          })
          return null
        }
      },

      loadShoppingList: async (mealPlanId) => {
        set({ loadingShoppingList: true, error: null })
        try {
          const shoppingLists = await shoppingListApi.getShoppingLists(undefined, mealPlanId)
          set({
            shoppingLists,
            currentShoppingList: shoppingLists[0] ?? null,
            loadingShoppingList: false,
          })
        } catch (error) {
          set({
            shoppingLists: [],
            currentShoppingList: null,
            loadingShoppingList: false,
            error: error instanceof Error ? error.message : 'Failed to load shopping list',
          })
        }
      },

      updateShoppingListItem: async (itemId, updates) => {
        const currentShoppingList = get().currentShoppingList
        if (!currentShoppingList) {
          return false
        }

        const itemIndex = parseInt(itemId.replace('item-', ''), 10)
        if (Number.isNaN(itemIndex)) {
          return false
        }

        set({ loadingShoppingList: true, error: null })
        try {
          const updated = await shoppingListApi.updateShoppingListItem(currentShoppingList.id, itemIndex, updates)
          set({ currentShoppingList: updated, loadingShoppingList: false })
          return true
        } catch (error) {
          set({
            loadingShoppingList: false,
            error: error instanceof Error ? error.message : 'Failed to update shopping list item',
          })
          return false
        }
      },

      addCustomShoppingItem: async (_mealPlanId, ingredient, quantity, unit) => {
        const currentShoppingList = get().currentShoppingList
        if (!currentShoppingList) {
          return null
        }

        const newItem: ShoppingListItem = {
          name: ingredient,
          ingredient,
          quantity,
          unit,
          is_purchased: false,
          recipe_ids: [],
          recipe_titles: [],
        }

        const updated = await shoppingListApi.updateShoppingList(currentShoppingList.id, {
          ingredients: [...currentShoppingList.ingredients, newItem],
        })

        set({ currentShoppingList: updated })
        return newItem
      },

      removeShoppingListItem: async (itemId) => {
        const currentShoppingList = get().currentShoppingList
        if (!currentShoppingList) {
          return false
        }

        const itemIndex = parseInt(itemId.replace('item-', ''), 10)
        if (Number.isNaN(itemIndex)) {
          return false
        }

        const updated = await shoppingListApi.updateShoppingList(currentShoppingList.id, {
          ingredients: currentShoppingList.ingredients.filter((_, index) => index !== itemIndex),
        })

        set({ currentShoppingList: updated })
        return true
      },

      toggleShoppingItemChecked: async (itemId) => {
        const currentShoppingList = get().currentShoppingList
        if (!currentShoppingList) {
          return false
        }

        const itemIndex = parseInt(itemId.replace('item-', ''), 10)
        const currentItem = currentShoppingList.ingredients[itemIndex]
        if (!currentItem) {
          return false
        }

        return get().updateShoppingListItem(itemId, {
          checked: !currentItem.checked,
          is_purchased: !currentItem.is_purchased,
        })
      },

      clearCheckedItems: async (shoppingListId) => {
        const currentShoppingList = get().currentShoppingList
        if (!currentShoppingList || currentShoppingList.id !== shoppingListId) {
          return false
        }

        const updated = await shoppingListApi.updateShoppingList(shoppingListId, {
          ingredients: currentShoppingList.ingredients.filter((item) => !item.checked && !item.is_purchased),
        })

        set({ currentShoppingList: updated })
        return true
      },

      setCurrentWeek: (startDate) => {
        set({
          currentWeekStart: getWeekStart(startDate),
          currentWeekEnd: getWeekEnd(startDate),
        })
      },

      navigateToWeek: (direction) => {
        const current = new Date(get().currentWeekStart)
        current.setDate(current.getDate() + (direction === 'next' ? 7 : -7))
        get().setCurrentWeek(current)
      },

      navigateToToday: () => get().setCurrentWeek(new Date()),

      getWeekDates: (startDate) => {
        const start = getWeekStart(startDate ?? get().currentWeekStart)
        const end = getWeekEnd(start)
        const dates = Array.from({ length: 7 }, (_, index) => {
          const date = new Date(start)
          date.setDate(start.getDate() + index)
          return date
        })
        return { start, end, dates }
      },

      loadSuggestedRecipes: async (householdId, mealType) => {
        set({ loadingSuggestions: true, error: null })
        try {
          const suggestedRecipes = householdId ? await recipeApi.getMealPlanSuggestions(householdId, mealType) : []
          set({ suggestedRecipes, loadingSuggestions: false })
        } catch (error) {
          set({
            suggestedRecipes: [],
            loadingSuggestions: false,
            error: error instanceof Error ? error.message : 'Failed to load suggestions',
          })
        }
      },

      setFilters: (filters) => set({ filters }),
      clearFilters: () => set({ filters: {} }),

      getFilteredMealPlans: () => {
        const { mealPlans, filters } = get()
        return mealPlans.filter((mealPlan) => {
          if (filters.search) {
            const search = filters.search.toLowerCase()
            if (
              !mealPlan.name.toLowerCase().includes(search) &&
              !mealPlan.description?.toLowerCase().includes(search)
            ) {
              return false
            }
          }

          if (filters.start_date && mealPlan.start_date < filters.start_date) {
            return false
          }

          if (filters.end_date && mealPlan.end_date > filters.end_date) {
            return false
          }

          return true
        })
      },

      getMealConflicts: (date, mealType) =>
        get().plannedMeals.filter((meal) => meal.planned_date === date && meal.meal_type === mealType),

      resolveMealConflict: async (existingMealId, action) => {
        if (action === 'replace') {
          return get().removePlannedMeal(existingMealId)
        }
        if (action === 'duplicate') {
          return true
        }
        return false
      },

      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      getMealsForDate: (date) => get().plannedMeals.filter((meal) => meal.planned_date === date),

      getMealsForWeek: (startDate) => {
        const { dates } = get().getWeekDates(startDate)
        return dates.reduce<Record<string, PlannedMeal[]>>((acc, date) => {
          const dateStr = formatDate(date)
          acc[dateStr] = get().getMealsForDate(dateStr)
          return acc
        }, {})
      },

      getRecipeUsageStats: (recipeId) => {
        const recipeMeals = get().plannedMeals.filter((meal) => meal.recipe_id === recipeId)
        const lastUsed = recipeMeals.sort(
          (a, b) => new Date(b.planned_date).getTime() - new Date(a.planned_date).getTime()
        )[0]?.planned_date
        return { count: recipeMeals.length, lastUsed }
      },
    }),
    {
      name: 'meal-planning-storage',
      version: 2,
      partialize: (state) => ({
        currentMealPlan: state.currentMealPlan,
        filters: state.filters,
      }),
    }
  )
)
